import { IsNotEmpty, IsNumber, IsOptional, IsString, ValidateIf } from 'class-validator';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import backToListView from 'src/actions/shared/backToListView';
import createCollectionResource from 'src/actions/shared/createResource';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { IsPropertyUniqueInStoreSlice } from 'src/actions/validators/IsPropertyUniqueInStoreSlice';
import { logout } from 'src/actions/auth/authSlice';
import { toastError, toastSuccess } from 'src/actions/toast';
import { translateErrors } from 'src/adapters/validateType';

export class CustomChannel {
  id: string;
  archived: boolean;

  @IsString()
  @IsNotEmpty({ message: 'Please enter the name' })
  @IsPropertyUniqueInStoreSlice('customChannels', { message: 'This name already exists. Please create a new one.' })
  name: string;

  agentInitiatedConversations: boolean;
  allowAttachments: boolean;
  displayAddressInProfile: boolean;
  messageGroupingEnabled: boolean;

  @IsString()
  @IsNotEmpty({ message: 'Please select an Icon' })
  icon: string;
  @ValidateIf(props => !props.id)
  @IsNotEmpty({ message: 'Please select the Inbox' })
  routingGroupId: string;

  @IsOptional()
  @IsNumber()
  slaSeconds?: number;
}

const customChannelResource = createCollectionResource<CustomChannel>({
  name: 'customChannels',
  endpoint: 'v1/orgs/:orgId/custom-channels/:id',
  type: CustomChannel,
  observerCreator: ({ actions }) => {
    return ({ dispatch }) => {
      return {
        onBroadcast(item) {
          dispatch(actions.broadcast(item));
        },
      };
    };
  },
  options: {
    toastMessages: {
      onAdd: item => `"${item.name}" has been added.`,
      onAddFail: item => `Unable to add "${item.name}". Please try again.`,
      onUpdate: item => `"${item.name}" has been edited.`,
      onUpdateFail: item => `Unable to edit "${item.name}". Please try again.`,
    },
  },
});

const addWithDefaults: sharedTypes.AppThunk =
  (params: any, attrs: any) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(customChannelResource.actions.addStarted({}));

      const data = attrs;

      const channelsConfiguration = await gateways.channelsConfiguration.fetch({});
      const customChannel = await gateways.customChannels.add(params, data);

      const newConfiguration = {
        ...channelsConfiguration,
        [`CUSTOM_CHANNEL:${customChannel.id}`]: {
          defaultInbox: attrs.routingGroupId,
          ...(attrs.slaSeconds ? { defaultSLASeconds: attrs.slaSeconds } : {}),
        },
      };

      await gateways.channelsConfiguration.replace(newConfiguration);

      dispatch(customChannelResource.actions.addSuccess({}));
      backToListView();
      dispatch(toastSuccess(`"${attrs.name}" has been added.`));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(customChannelResource.actions.addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(customChannelResource.actions.addRequestError(err));
      }

      dispatch(toastError(`Unable to add "${attrs.name}". Please try again.`));
    }
  };

customChannelResource.effects.addWithDefaults = addWithDefaults;

export default customChannelResource;
